import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { locationAPI } from '../../utils/crmApi';
import {
    MapPinIcon,
    PlusIcon,
    EyeIcon,
    PencilIcon,
    TrashIcon,
    ChartBarIcon,
    HomeIcon,
    CurrencyDollarIcon,
    UserGroupIcon,
    AcademicCapIcon,
    HeartIcon,
    ClockIcon,
    ExclamationTriangleIcon,
    CheckCircleIcon,
    InformationCircleIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useDialog, ConfirmationDialog } from '../../components/kibo-ui/dialog';
import {
    TableProvider,
    Table,
    TableHeader,
    TableSearch,
    TableHead,
    TableBody,
    TableRow,
    TableHeaderCell,
    TableCell,
    TableFooter,
    StatusBadge,
    TableAvatar
} from '../../components/kibo-ui/table';

const Locations = () => {
    const { openDialog } = useDialog();
    const [locations, setLocations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [countryFilter, setCountryFilter] = useState('');
    const [regionFilter, setRegionFilter] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('');
    const [pagination, setPagination] = useState({ page: 1, limit: 20 });
    const [selectedLocations, setSelectedLocations] = useState([]);
    const [bulkActionLoading, setBulkActionLoading] = useState(false);

    // Location category colors
    const getCategoryColor = (category) => {
        const colors = {
            'city': 'bg-blue-100 text-blue-700',
            'region': 'bg-green-100 text-green-700',
            'country': 'bg-purple-100 text-purple-700',
            'neighborhood': 'bg-yellow-100 text-yellow-700',
            'landmark': 'bg-red-100 text-red-700',
            'business': 'bg-muted text-foreground'
        };
        return colors[category] || 'bg-muted text-foreground';
    };

    // Location category icons
    const getCategoryIcon = (category) => {
        const icons = {
            'city': '🏙️',
            'region': '🗺️',
            'country': '🌍',
            'neighborhood': '🏘️',
            'landmark': '🏛️',
            'business': '🏢'
        };
        return icons[category] || '📍';
    };

    useEffect(() => {
        loadLocations();
    }, [search, countryFilter, regionFilter, categoryFilter, pagination.page]);

    const loadLocations = async () => {
        try {
            setLoading(true);
            const params = {
                page: pagination.page,
                limit: pagination.limit,
                ...(search && { search }),
                ...(countryFilter && { country: countryFilter }),
                ...(regionFilter && { region: regionFilter }),
                ...(categoryFilter && { category: categoryFilter })
            };

            const response = await locationAPI.getAll(params);
            if (response.data.success) {
                setLocations(response.data.data);
                if (response.data.pagination) {
                    setPagination(response.data.pagination);
                }
            }
        } catch (error) {
            console.error('Failed to load locations:', error);
            toast.error('Failed to load locations');
        } finally {
            setLoading(false);
        }
    };

    const handleSelectLocation = (locationId) => {
        setSelectedLocations(prev => 
            prev.includes(locationId) 
                ? prev.filter(id => id !== locationId)
                : [...prev, locationId]
        );
    };

    const handleSelectAll = () => {
        if (selectedLocations.length === locations.length) {
            setSelectedLocations([]);
        } else {
            setSelectedLocations(locations.map(location => location._id));
        }
    };

    const handleBulkAction = (action) => {
        if (selectedLocations.length === 0) return;
        
        const actionMessages = {
            'export': 'export',
            'archive': 'archive',
            'delete': 'delete'
        };

        openDialog({
            title: `${actionMessages[action]} Locations`,
            description: `Are you sure you want to ${actionMessages[action]} ${selectedLocations.length} location(s)?`,
            confirmText: actionMessages[action].charAt(0).toUpperCase() + actionMessages[action].slice(1),
            cancelText: 'Cancel',
            variant: action === 'delete' ? 'danger' : 'warning',
            onConfirm: async () => {
                try {
                    setBulkActionLoading(true);
                    await locationAPI.bulkAction(selectedLocations, action);
                    toast.success(`${selectedLocations.length} location(s) ${actionMessages[action]}ed successfully`);
                    setSelectedLocations([]);
                    loadLocations();
                } catch (error) {
                    console.error('Error performing bulk action:', error);
                    toast.error('Failed to perform bulk action');
                } finally {
                    setBulkActionLoading(false);
                }
            }
        });
    };

    const handleDeleteLocation = (locationId) => {
        openDialog({
            title: 'Delete Location',
            description: 'Are you sure you want to delete this location? This action cannot be undone.',
            confirmText: 'Delete Location',
            cancelText: 'Cancel',
            variant: 'danger',
            onConfirm: async () => {
                try {
                    await locationAPI.delete(locationId);
                    toast.success('Location deleted successfully');
                    loadLocations();
                } catch (error) {
                    console.error('Error deleting location:', error);
                    toast.error('Failed to delete location');
                }
            }
        });
    };

    const columns = [
        { accessor: 'select', label: '', sortable: false, width: 'w-12' },
        { accessor: 'name', label: 'Location', sortable: true },
        { accessor: 'category', label: 'Category', sortable: true },
        { accessor: 'country', label: 'Country', sortable: true },
        { accessor: 'region', label: 'Region', sortable: true },
        { accessor: 'insights', label: 'Key Insights', sortable: true },
        { accessor: 'cost', label: 'Cost of Living', sortable: true },
        { accessor: 'actions', label: 'Actions', sortable: false }
    ];

    const categoryOptions = [
        { value: '', label: 'All Categories' },
        { value: 'city', label: 'City' },
        { value: 'region', label: 'Region' },
        { value: 'country', label: 'Country' },
        { value: 'neighborhood', label: 'Neighborhood' },
        { value: 'landmark', label: 'Landmark' },
        { value: 'business', label: 'Business' }
    ];

    const countryOptions = [
        { value: '', label: 'All Countries' },
        { value: 'Spain', label: 'Spain' },
        { value: 'France', label: 'France' },
        { value: 'Germany', label: 'Germany' },
        { value: 'Italy', label: 'Italy' },
        { value: 'Portugal', label: 'Portugal' },
        { value: 'Netherlands', label: 'Netherlands' },
        { value: 'Belgium', label: 'Belgium' },
        { value: 'Austria', label: 'Austria' },
        { value: 'Switzerland', label: 'Switzerland' },
        { value: 'United Kingdom', label: 'United Kingdom' },
        { value: 'Ireland', label: 'Ireland' },
        { value: 'United States', label: 'United States' },
        { value: 'Canada', label: 'Canada' },
        { value: 'Australia', label: 'Australia' },
        { value: 'New Zealand', label: 'New Zealand' }
    ];

    const regionOptions = [
        { value: '', label: 'All Regions' },
        { value: 'Madrid', label: 'Madrid' },
        { value: 'Barcelona', label: 'Barcelona' },
        { value: 'Valencia', label: 'Valencia' },
        { value: 'Seville', label: 'Seville' },
        { value: 'Paris', label: 'Paris' },
        { value: 'Lyon', label: 'Lyon' },
        { value: 'Marseille', label: 'Marseille' },
        { value: 'Berlin', label: 'Berlin' },
        { value: 'Munich', label: 'Munich' },
        { value: 'Hamburg', label: 'Hamburg' },
        { value: 'Rome', label: 'Rome' },
        { value: 'Milan', label: 'Milan' },
        { value: 'Lisbon', label: 'Lisbon' },
        { value: 'Porto', label: 'Porto' }
    ];

    const getCostOfLivingLevel = (costIndex) => {
        if (costIndex >= 80) return { level: 'Very High', color: 'text-red-600 bg-red-50' };
        if (costIndex >= 60) return { level: 'High', color: 'text-orange-600 bg-orange-50' };
        if (costIndex >= 40) return { level: 'Medium', color: 'text-yellow-600 bg-yellow-50' };
        if (costIndex >= 20) return { level: 'Low', color: 'text-green-600 bg-green-50' };
        return { level: 'Very Low', color: 'text-blue-600 bg-blue-50' };
    };

    const getKeyInsights = (location) => {
        const insights = [];
        if (location.population) insights.push(`Pop: ${location.population.toLocaleString()}`);
        if (location.unemploymentRate) insights.push(`Unemployment: ${location.unemploymentRate}%`);
        if (location.averageSalary) insights.push(`Avg Salary: €${location.averageSalary.toLocaleString()}`);
        if (location.crimeRate) insights.push(`Crime Rate: ${location.crimeRate}/100`);
        return insights.slice(0, 2).join(' • ');
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-foreground">Location Intelligence</h1>
                    <p className="mt-1 text-sm text-muted-foreground">
                        Market insights and data for relocation destinations
                    </p>
                </div>
                <div className="mt-4 sm:mt-0 flex items-center gap-3">
                    {selectedLocations.length > 0 && (
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => handleBulkAction('export')}
                                disabled={bulkActionLoading}
                                className="inline-flex items-center px-3 py-2 text-sm font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 disabled:opacity-50"
                            >
                                <ChartBarIcon className="h-4 w-4 mr-1" />
                                Export
                            </button>
                            <button
                                onClick={() => handleBulkAction('archive')}
                                disabled={bulkActionLoading}
                                className="inline-flex items-center px-3 py-2 text-sm font-medium text-foreground bg-muted border border-border rounded-lg hover:bg-muted disabled:opacity-50"
                            >
                                <ClockIcon className="h-4 w-4 mr-1" />
                                Archive
                            </button>
                            <button
                                onClick={() => handleBulkAction('delete')}
                                disabled={bulkActionLoading}
                                className="inline-flex items-center px-3 py-2 text-sm font-medium text-red-700 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 disabled:opacity-50"
                            >
                                <TrashIcon className="h-4 w-4 mr-1" />
                                Delete
                            </button>
                        </div>
                    )}
                    <Link
                        to="/locations/new"
                        className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ring"
                    >
                        <PlusIcon className="h-5 w-5 mr-2" />
                        Add Location
                    </Link>
                </div>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-card rounded-lg shadow-sm border border-border p-4">
                    <div className="flex items-center">
                        <div className="flex-shrink-0">
                            <MapPinIcon className="h-8 w-8 text-blue-600" />
                        </div>
                        <div className="ml-4">
                            <p className="text-sm font-medium text-muted-foreground">Total Locations</p>
                            <p className="text-2xl font-semibold text-foreground">{locations.length}</p>
                        </div>
                    </div>
                </div>
                <div className="bg-card rounded-lg shadow-sm border border-border p-4">
                    <div className="flex items-center">
                        <div className="flex-shrink-0">
                            <HomeIcon className="h-8 w-8 text-green-600" />
                        </div>
                        <div className="ml-4">
                            <p className="text-sm font-medium text-muted-foreground">Cities</p>
                            <p className="text-2xl font-semibold text-foreground">
                                {locations.filter(l => l.category === 'city').length}
                            </p>
                        </div>
                    </div>
                </div>
                <div className="bg-card rounded-lg shadow-sm border border-border p-4">
                    <div className="flex items-center">
                        <div className="flex-shrink-0">
                            <CurrencyDollarIcon className="h-8 w-8 text-purple-600" />
                        </div>
                        <div className="ml-4">
                            <p className="text-sm font-medium text-muted-foreground">Avg Cost Index</p>
                            <p className="text-2xl font-semibold text-foreground">
                                {locations.length > 0 ? 
                                    Math.round(locations.reduce((sum, l) => sum + (l.costOfLivingIndex || 0), 0) / locations.length) : 
                                    0
                                }
                            </p>
                        </div>
                    </div>
                </div>
                <div className="bg-card rounded-lg shadow-sm border border-border p-4">
                    <div className="flex items-center">
                        <div className="flex-shrink-0">
                            <UserGroupIcon className="h-8 w-8 text-orange-600" />
                        </div>
                        <div className="ml-4">
                            <p className="text-sm font-medium text-muted-foreground">Total Population</p>
                            <p className="text-2xl font-semibold text-foreground">
                                {locations.reduce((sum, l) => sum + (l.population || 0), 0).toLocaleString()}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div className="bg-card rounded-lg shadow-sm border border-border p-4">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-foreground mb-1">Category</label>
                        <select
                            value={categoryFilter}
                            onChange={(e) => setCategoryFilter(e.target.value)}
                            className="w-full border border-input bg-background text-foreground rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                        >
                            {categoryOptions.map(option => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-foreground mb-1">Country</label>
                        <select
                            value={countryFilter}
                            onChange={(e) => setCountryFilter(e.target.value)}
                            className="w-full border border-input bg-background text-foreground rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                        >
                            {countryOptions.map(option => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-foreground mb-1">Region</label>
                        <select
                            value={regionFilter}
                            onChange={(e) => setRegionFilter(e.target.value)}
                            className="w-full border border-input bg-background text-foreground rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                        >
                            {regionOptions.map(option => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="flex items-end">
                        <button
                            onClick={() => {
                                setCategoryFilter('');
                                setCountryFilter('');
                                setRegionFilter('');
                                setSearch('');
                            }}
                            className="w-full px-4 py-2 text-sm font-medium text-foreground bg-muted border border-input focus:ring-2 focus:ring-ring focus:border-ring bg-background text-foreground rounded-lg hover:bg-muted"
                        >
                            Clear Filters
                        </button>
                    </div>
                </div>
            </div>

            {/* Enhanced Table with Kibo UI */}
            <TableProvider 
                data={locations} 
                columns={columns}
                onSort={(key, direction) => console.log('🔄 Table sort:', { key, direction })}
                onFilter={(key, value) => console.log('🔍 Table filter:', { key, value })}
            >
                <Table>
                    <TableHeader>
                        <TableSearch placeholder="Search locations by name, country, or insights..." />
                    </TableHeader>
                    
                    <div className="bg-card rounded-xl shadow-sm border border-border overflow-hidden">
                        <table className="min-w-full divide-y divide-gray-200">
                            <TableHead>
                                <tr>
                                    {columns.map((column) => (
                                        <TableHeaderCell key={column.accessor} column={column}>
                                            {column.accessor === 'select' ? (
                                                <input
                                                    type="checkbox"
                                                    checked={selectedLocations.length === locations.length && locations.length > 0}
                                                    onChange={handleSelectAll}
                                                    className="h-4 w-4 text-blue-600 focus:ring-ring border-input rounded"
                                                />
                                            ) : (
                                                column.label
                                            )}
                                        </TableHeaderCell>
                                    ))}
                                </tr>
                            </TableHead>
                            <TableBody>
                                {loading ? (
                                    <tr>
                                        <td colSpan={columns.length} className="px-6 py-12 text-center">
                                            <div className="flex items-center justify-center">
                                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                                            </div>
                                        </td>
                                    </tr>
                                ) : locations.length === 0 ? (
                                    <tr>
                                        <td colSpan={columns.length} className="px-6 py-12 text-center">
                                            <div className="text-center">
                                                <MapPinIcon className="mx-auto h-12 w-12 text-muted-foreground" />
                                                <h3 className="mt-2 text-sm font-medium text-foreground">No locations found</h3>
                                                <p className="mt-1 text-sm text-muted-foreground">
                                                    Get started by adding your first location.
                                                </p>
                                                <Link
                                                    to="/locations/new"
                                                    className="mt-4 inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                                                >
                                                    <PlusIcon className="h-5 w-5 mr-2" />
                                                    Add Location
                                                </Link>
                                            </div>
                                        </td>
                                    </tr>
                                ) : (
                                    locations.map((location) => {
                                        const costLevel = getCostOfLivingLevel(location.costOfLivingIndex || 0);
                                        return (
                                            <TableRow key={location._id}>
                                                <TableCell className="w-12">
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedLocations.includes(location._id)}
                                                        onChange={() => handleSelectLocation(location._id)}
                                                        className="h-4 w-4 text-blue-600 focus:ring-ring border-input rounded"
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <div className="flex items-center">
                                                        <div className="text-2xl mr-3">{getCategoryIcon(location.category)}</div>
                                                        <div>
                                                            <Link
                                                                to={`/locations/${location._id}`}
                                                                className="text-sm font-medium text-foreground hover:text-blue-600"
                                                            >
                                                                {location.name}
                                                            </Link>
                                                            <div className="text-xs text-muted-foreground">
                                                                {location.description}
                                                            </div>
                                                            {location.coordinates && (
                                                                <div className="text-xs text-muted-foreground">
                                                                    📍 {location.coordinates.lat}, {location.coordinates.lng}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </TableCell>
                                                <TableCell>
                                                    <StatusBadge 
                                                        status={location.category}
                                                        className={getCategoryColor(location.category)}
                                                    >
                                                        {location.category.charAt(0).toUpperCase() + location.category.slice(1)}
                                                    </StatusBadge>
                                                </TableCell>
                                                <TableCell>
                                                    <div className="text-sm text-foreground">{location.country}</div>
                                                    <div className="text-xs text-muted-foreground">{location.region}</div>
                                                </TableCell>
                                                <TableCell>
                                                    <div className="text-sm text-foreground">{location.region}</div>
                                                    <div className="text-xs text-muted-foreground">{location.timezone}</div>
                                                </TableCell>
                                                <TableCell>
                                                    <div className="text-sm text-foreground">
                                                        {getKeyInsights(location)}
                                                    </div>
                                                    <div className="text-xs text-muted-foreground">
                                                        {location.climate && `Climate: ${location.climate}`}
                                                    </div>
                                                </TableCell>
                                                <TableCell>
                                                    <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${costLevel.color}`}>
                                                        <CurrencyDollarIcon className="h-3 w-3 mr-1" />
                                                        {costLevel.level}
                                                    </div>
                                                    <div className="text-xs text-muted-foreground mt-1">
                                                        Index: {location.costOfLivingIndex || 0}/100
                                                    </div>
                                                </TableCell>
                                                <TableCell>
                                                    <div className="flex items-center gap-2">
                                                        <Link
                                                            to={`/locations/${location._id}`}
                                                            className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                                                            title="View Details"
                                                        >
                                                            <EyeIcon className="h-4 w-4" />
                                                        </Link>
                                                        <Link
                                                            to={`/locations/${location._id}/edit`}
                                                            className="text-muted-foreground hover:text-foreground text-sm font-medium"
                                                            title="Edit"
                                                        >
                                                            <PencilIcon className="h-4 w-4" />
                                                        </Link>
                                                        <button
                                                            onClick={() => handleDeleteLocation(location._id)}
                                                            className="text-red-600 hover:text-red-800 text-sm font-medium"
                                                            title="Delete"
                                                        >
                                                            <TrashIcon className="h-4 w-4" />
                                                        </button>
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })
                                )}
                            </TableBody>
                        </table>
                    </div>
                    
                    <TableFooter>
                        <div className="px-6 py-3 border-t border-border">
                            <div className="flex items-center justify-between">
                                <div className="text-sm text-foreground">
                                    Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} results
                                </div>
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                                        disabled={pagination.page === 1}
                                        className="px-3 py-1 text-sm text-foreground bg-card border border-input focus:ring-2 focus:ring-ring focus:border-ring bg-background text-foreground rounded hover:bg-muted disabled:opacity-50"
                                    >
                                        Previous
                                    </button>
                                    <span className="px-3 py-1 text-sm text-foreground">
                                        Page {pagination.page} of {pagination.pages}
                                    </span>
                                    <button
                                        onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                                        disabled={pagination.page === pagination.pages}
                                        className="px-3 py-1 text-sm text-foreground bg-card border border-input focus:ring-2 focus:ring-ring focus:border-ring bg-background text-foreground rounded hover:bg-muted disabled:opacity-50"
                                    >
                                        Next
                                    </button>
                                </div>
                            </div>
                        </div>
                    </TableFooter>
                </Table>
            </TableProvider>
        </div>
    );
};

export default Locations;
